import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Toast system — replaces alert() across the app.
// Usage:
//   import { useToast } from '../components/Toast';
//   const toast = useToast();
//   toast.success('Gespeichert');
//   toast.error('Fehlgeschlagen: ' + msg);

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback((variant, message, opts = {}) => {
    const id = nextId++;
    const duration = opts.duration ?? (variant === 'error' ? 6000 : 3500);
    setToasts((curr) => [...curr, { id, variant, message }]);
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  const api = useRef({
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    dismiss,
  }).current;

  useEffect(() => {
    const tmap = timers.current;
    return () => { for (const t of tmap.values()) clearTimeout(t); };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-container" role="region" aria-live="polite" aria-label="Benachrichtigungen">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.variant}`} role={t.variant === 'error' ? 'alert' : 'status'}>
            <span className="toast-message">{t.message}</span>
            <button
              type="button"
              className="toast-close"
              aria-label="Schließen"
              onClick={() => dismiss(t.id)}
            >×</button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback no-op so the app still renders if the provider is missing
    return {
      success: (msg) => console.log('[toast.success]', msg),
      error:   (msg) => console.error('[toast.error]', msg),
      info:    (msg) => console.log('[toast.info]', msg),
      warning: (msg) => console.warn('[toast.warning]', msg),
      dismiss: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
